import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { UiBadgeVariant } from '../../models/ui-badge.model';
import { UiTableColumn, getBadgeVariantForStatus, getBadgeVariantForType } from '../../models/ui-table.model';

@Component({
  selector: 'app-ui-data-table',
  templateUrl: './ui-data-table.component.html',
  styleUrls: ['./ui-data-table.component.scss']
})
export class UiDataTableComponent {
  @Input() columns: UiTableColumn[] = [];
  @Input() rows: Record<string, unknown>[] = [];
  @Input() sortColumn = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  @Input() virtualScroll = false;
  @Input() itemSize = 60;
  @Input() viewportHeight = '500px';
  @Output() sort = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
  @Output() rowClick = new EventEmitter<Record<string, unknown>>();
  @Output() linkClick = new EventEmitter<{ row: Record<string, unknown>; column: string }>();
  @Output() actionClick = new EventEmitter<{ row: Record<string, unknown>; type: string }>();
  @Output() nearEnd = new EventEmitter<void>();
  @Output() checkboxChange = new EventEmitter<{ row: Record<string, unknown>; checked: boolean }>();
  @Output() selectAllChange = new EventEmitter<boolean>();

  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

  get allSelectableSelected(): boolean {
    const selectable = this.selectableRows;
    return selectable.length > 0 && selectable.every(r => !!r['selected']);
  }

  get someSelectableSelected(): boolean {
    const selectable = this.selectableRows;
    const selectedCount = selectable.filter(r => !!r['selected']).length;
    return selectedCount > 0 && selectedCount < selectable.length;
  }

  /** Avoid a tall empty white box when there are few rows. */
  get resolvedViewportHeight(): string {
    const configured = parseInt(String(this.viewportHeight), 10);
    const maxHeight = Number.isFinite(configured) && configured > 0 ? configured : 500;
    const rowCount = Math.max((this.rows || []).length, 1);
    const contentHeight = rowCount * this.itemSize + 8;
    return `${Math.min(contentHeight, maxHeight)}px`;
  }

  /**
   * Track is at least as wide as the sum of readable column mins.
   * Uses CSS max() so it also fills the card when the card is wider.
   * Parent __scroll stays viewport-bound → inner horizontal scroll only.
   */
  get trackMinWidth(): string {
    const total = (this.columns || []).reduce((sum, col) => sum + this.getColumnMinWidthPx(col), 0);
    return `max(100%, ${Math.max(total, 640)}px)`;
  }

  private get selectableRows(): Record<string, unknown>[] {
    return (this.rows || []).filter(r => !r['selectionDisabled']);
  }

  getColumnWidth(col: UiTableColumn): string {
    return col.width || `${this.getColumnMinWidthPx(col)}px`;
  }

  getColumnMinWidth(col: UiTableColumn): string {
    return `${this.getColumnMinWidthPx(col)}px`;
  }

  getCellTitle(row: Record<string, unknown>, col: UiTableColumn): string | null {
    if (col.type === 'actions' || col.type === 'checkbox') {
      return null;
    }
    const value = this.getCellValue(row, col.key);
    if (value == null || value === '') {
      return null;
    }
    if (col.type === 'date') {
      return this.formatDate(value);
    }
    if (typeof value === 'object') {
      return null;
    }
    return String(value);
  }

  getCellValue(row: Record<string, unknown>, key: string): unknown {
    return this.getNestedValue(row, key);
  }

  getBadgeVariant(row: Record<string, unknown>, col: UiTableColumn): UiBadgeVariant {
    const val = String(this.getCellValue(row, col.badgeKey || col.key) || '');
    if (col.key === 'status' || col.badgeKey === 'status') {
      return getBadgeVariantForStatus(val);
    }
    if (col.key === 'type' || col.badgeKey === 'type') {
      return getBadgeVariantForType(val);
    }
    return getBadgeVariantForStatus(val);
  }

  onSort(col: UiTableColumn): void {
    if (!col.sortable) return;
    let direction: 'asc' | 'desc' = 'asc';
    if (this.sortColumn === col.key) {
      direction = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }
    this.sort.emit({ column: col.key, direction });
  }

  onRowClick(row: Record<string, unknown>): void {
    this.rowClick.emit(row);
  }

  onLinkClick(row: Record<string, unknown>, column: string, event: MouseEvent): void {
    event.stopPropagation();
    this.linkClick.emit({ row, column });
  }

  onActionClick(row: Record<string, unknown>, type: string): void {
    this.actionClick.emit({ row, type });
  }

  onCheckboxChange(row: Record<string, unknown>, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checkboxChange.emit({ row, checked });
  }

  onSelectAllChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectAllChange.emit(checked);
  }

  onVirtualScroll(): void {
    if (!this.viewport) return;
    const distanceFromBottom = this.viewport.measureScrollOffset('bottom');
    if (distanceFromBottom < 100) {
      this.nearEnd.emit();
    }
  }

  formatDate(value: unknown): string {
    if (!value) return '—';
    const d = new Date(String(value));
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private getColumnMinWidthPx(col: UiTableColumn): number {
    if (col.width) {
      const match = String(col.width).match(/(\d+(\.\d+)?)/);
      if (match) {
        return Math.max(Number(match[1]), this.defaultMinWidthPx(col));
      }
    }
    return this.defaultMinWidthPx(col);
  }

  /** Readable mins — keeps headers/cells usable when zoomed. */
  private defaultMinWidthPx(col: UiTableColumn): number {
    switch (col.type) {
      case 'checkbox':
        return 48;
      case 'actions':
        return 88;
      case 'date':
        return 120;
      case 'badge':
        return 120;
      case 'link':
        return 150;
      default:
        return 140;
    }
  }

  private getNestedValue(obj: Record<string, unknown>, key: string): unknown {
    return key.split('.').reduce((acc: unknown, part) => {
      if (acc && typeof acc === 'object') {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj);
  }
}
